import React, { useState, useEffect, useContext } from 'react';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import DownloadUtils from '../../../utils/download-utils';
import ImageCacheContext from './image-cache-context';
import { toJS } from 'mobx';

function HandoutImageManager({ handoutData, children }) {
    const { imageStore } = useContext(StoreContext);
    const [recentlyCachedUrls, setRecentlyCachedUrls] = useState(new Set());

    useEffect(() => {
        loadOrFetchImages();
    }, [handoutData]); // Ensure this effect runs when handoutData changes

    function extractImageUrls() {
        console.log('extractImageUrls');
        const extractedUrls = [];
        handoutData.contentData['en-UK'].forEach((section) => {
            section.blocks.forEach((block) => {
                if (block.type === 'iconStrip') {
                    block.iconDetails.forEach((icon) => extractedUrls.push(icon.img));
                } else if (block.type === 'illustration') {
                    extractedUrls.push(block.src);
                }
            });
        });

        // Include whiteboard main illustration if it's a whiteboard
        if (handoutData.canvasImageLink && !extractedUrls.includes(handoutData.canvasImageLink)) {
            console.warn(
                'Adding main illustration (canvasImageLink) to extracted images:',
                handoutData.canvasImageLink,
            );
            extractedUrls.push(handoutData.canvasImageLink);
        }
        if (
            handoutData.attachedWhiteboardCanvasImageLink &&
            !extractedUrls.includes(handoutData.attachedWhiteboardCanvasImageLink)
        ) {
            extractedUrls.push(handoutData.attachedWhiteboardCanvasImageLink);
        }

        return extractedUrls;
    }

    function findPresignedUrlForImage(imageUrl) {
        // decode the presignedUrl when checking to ensure matching format in the imageUrl e.g. ' is %27 then check if it included in array
        const match = handoutData.urls.getUrls.find((u) => decodeURIComponent(u.presignedUrl).includes(imageUrl));
        return match ? match.presignedUrl : null;
    }

    async function loadOrFetchImages() {
        const imageUrls = extractImageUrls(handoutData);
        console.log('loadOrFetchImages imageUrls: ', imageUrls);

        // Attempt to load images from IndexedDB
        const imageObjects = await Promise.all(imageUrls.map((url) => imageStore.getImageFromIndexedDB(url)));

        // Log the results
        const cachedImages = imageObjects.filter((obj) => obj !== null);
        console.log(`number of cached images: ${cachedImages.length}`);
        console.log('Cached images:', cachedImages);

        // Extract URLs for which the images were not found in IndexedDB
        const uncachedUrls = imageUrls.filter((url, index) => !imageObjects[index]);
        console.log(`uncachedUrls json: ${JSON.stringify(uncachedUrls, 2, null)}`);

        const isPresigned = (u) => /^https?:\/\//i.test(u);

        if (uncachedUrls.length) {
            const downloadPromises = uncachedUrls.map(async (url) => {
                const presignedUrl = isPresigned(url) ? url : findPresignedUrlForImage(url);
                if (presignedUrl) {
                    console.warn('Downloading uncached image:', presignedUrl);
                    return DownloadUtils.downloadImageFromPresignedUrl(presignedUrl)
                        .then((downloadResult) =>
                            imageStore.saveImageToIndexedDB(url, downloadResult.base64data, downloadResult.eTag),
                        )
                        .then(() => ({ status: 'fulfilled', url }))
                        .catch((error) => ({ status: 'rejected', url, reason: error }));
                }
                return Promise.resolve({ status: 'rejected', url, reason: 'No presigned URL found' });
            });

            // Use Promise.allSettled to wait for all the download and save operations to complete
            const downloadResults = await Promise.allSettled(downloadPromises);

            // Process only successful downloads
            const successfulUrls = downloadResults
                .filter((result) => result.status === 'fulfilled')
                .map((result) => result.url);

            // Update the list of recently cached URLs with successfully downloaded URLs
            setRecentlyCachedUrls((prevUrls) => new Set([...prevUrls, ...successfulUrls]));
        }
    }

    return <ImageCacheContext.Provider value={recentlyCachedUrls}>{children}</ImageCacheContext.Provider>;
}

export default HandoutImageManager;
